<template>
  <div
    v-auto-animate
    class="bg-white rounded-md relative"
  >
    <!-- <div class="absolute right-0 -mt-12">
      <span
        class="cursor-pointer"
        @click="address.isOverseasAddress = !address.isOverseasAddress, showIndividualFields = !showIndividualFields"
      >
        {{ address.isOverseasAddress ? 'Select Australia address' : 'Not in Australia? ' }}
      </span>
    </div> -->
    <div
      v-show="!address.isOverseasAddress"
      class="flex flex-row gap-2"
    >
      <InputText
        id="origin"
        ref="autocompleteInput"
        v-model="autocompleteValue"
        class="w-full"
        placeholder="Enter a location"
        :disabled="disabledInputField"
        :invalid="isErrored"
      />
      <div class="flex justify-center items-center hover:scale-110">
        <i
          :class="showIndividualFields ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
          @click="toggleShowInputs"
        />
      </div>
    </div>
    <div v-if="showIndividualFields">
      <div class="flex flex-col gap-2 mt-4">
        <div class="w-full">
          <span class="text-primary text-sm ml-1">Street Number</span>
          <InputText
            id="street_number"
            v-model="address.streetNumber"
            class="w-full"
            :disabled="address.isOverseasAddress ? false : true"
          />
        </div>
        <div class="w-full">
          <span class="text-primary text-sm ml-1">Street Name</span>
          <InputText
            id="route"
            v-model="address.streetName"
            class="w-full"
            :disabled="address.isOverseasAddress ? false : true"
          />
        </div>
        <div class="w-full">
          <span class="text-primary text-sm  ml-1">Suburb</span>
          <InputText
            id="suburb"
            v-model="address.suburb"
            class="w-full"
            :disabled="address.isOverseasAddress ? false : true"
          />
        </div>
        <div class="w-full">
          <span class="text-primary text-sm  ml-1">Postal Code</span>
          <InputText
            id="postal_code"
            ref="postalField"
            v-model="address.postalCode"
            class="w-full"
            :disabled="address.isOverseasAddress ? false : true"
          />
        </div>
        <div class="w-full">
          <span class="text-primary text-sm  ml-1">State</span>
          <InputText
            id="state"
            v-model="address.state"
            class="w-full"
            :disabled="address.isOverseasAddress ? false : true"
          />
        </div>
        <div class="w-full">
          <span class="text-primary text-sm  ml-1">Country</span>
          <InputText
            id="country"
            v-model="address.country"
            class="w-full"
            :disabled="address.isOverseasAddress ? false : true"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'

interface Address {
  fullAddress: string
  streetNumber: string
  streetName: string
  postalCode: string
  suburb: string
  state: string
  country: string
  lat: number
  lng: number
  isOverseasAddress: boolean
}

const nuxtApp = useNuxtApp()

const showIndividualFields = ref(false)
const autocompleteValue = ref('')
const autocompleteInput = ref()

const address = defineModel<Address>('address', {
  required: true,
  default: () => ({
    fullAddress: '',
    streetNumber: '',
    streetName: '',
    postalCode: '',
    suburb: '',
    state: '',
    country: '',
    lat: 0,
    lng: 0,
    isOverseasAddress: false,
  }),
})

const changeAddress = () => {
  if (address.value.isOverseasAddress) {
    address.value.fullAddress = `${address.value.streetNumber} ${address.value.streetName}, ${address.value.suburb}, ${address.value.state}, ${address.value.country}, ${address.value.postalCode}`
  }
}

watch(address, (newValue, oldValue) => {
  if (newValue.fullAddress !== oldValue?.fullAddress && newValue.fullAddress) {
    autocompleteValue.value = newValue.fullAddress
  }

  if (address.value.isOverseasAddress) {
    changeAddress()
  }
}, { deep: true, immediate: true })

const disabledInputField = defineModel<boolean>('disabledInputField', {
  required: true,
  default: true,
})

const isErrored = defineModel<boolean>('isErrored', {
  required: false,
  default: false,
})

onMounted(() => {
  const googleMapsLoader = nuxtApp.$googleMapsLoader

  googleMapsLoader.load().then(() => {
    initializeAutocomplete()
  })
})

const initializeAutocomplete = () => {
  const autocomplete = new window.google.maps.places.Autocomplete(autocompleteInput.value.$el, {
    // componentRestrictions: { country: 'au' },
    fields: ['address_components', 'geometry'],
    types: ['address'],
  })

  autocomplete.addListener('place_changed', () => {
    // fillInAddress(autocomplete)
    const formattedPrediction = autocompleteInput.value.$el.value
    fillInAddress(formattedPrediction, autocomplete)
  })
}

const toggleShowInputs = () => {
  showIndividualFields.value = !showIndividualFields.value
}

const fillInAddress = (formattedPrediction: string, autocomplete: any) => {
  const place = autocomplete.getPlace()
  // if (!place.geometry) {
  //   nuxtApp.$toast.error("Please select a valid address");
  //   return;
  // }

  const latitude = place.geometry.location.lat()
  const longitude = place.geometry.location.lng()

  let streetNumber = ''
  let streetName = ''
  let suburb = ''
  let postalCode = ''
  let tempState = ''
  let tempCountry = ''

  // Extract desired components from the place result.
  place.address_components.forEach((component: any) => {
    const { types, long_name } = component

    if (types.includes('street_number')) {
      streetNumber = long_name
    }
    else if (types.includes('route')) {
      streetName = long_name
    }
    else if (types.includes('sublocality_level_1') || types.includes('locality')) {
      suburb = long_name
    }
    else if (types.includes('postal_code')) {
      postalCode = long_name
    }
    else if (types.includes('administrative_area_level_1')) {
      tempState = long_name
    }
    else if (types.includes('country')) {
      tempCountry = long_name
    }
  })

  // Update the address model.
  address.value = {
    fullAddress: formattedPrediction,
    streetNumber,
    streetName,
    suburb,
    postalCode,
    state: tempState,
    country: tempCountry,
    lat: latitude,
    lng: longitude,
    isOverseasAddress: false,
  }
}

const clearAddress = () => {
  address.value = {
    fullAddress: '',
    streetNumber: '',
    streetName: '',
    postalCode: '',
    suburb: '',
    state: '',
    country: '',
    lat: 0,
    lng: 0,
    isOverseasAddress: false,
  }
}

const hasAllFields = () => {
  if (!address.value.streetNumber || !address.value.streetName || !address.value.postalCode || !address.value.suburb || !address.value.state || !address.value.country) {
    return false
  }
  return true
}
</script>

<style>
.autocomplete-input {
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 5px;
    width: 100%;
}

.pac-container,
.pac-item {
    z-index: 2147483647 !important;
}
</style>
